import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RoomsManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', room_number: '', bags: '' });
  const [renameRoom, setRenameRoom] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameBags, setRenameBags] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/rooms`, { headers: auth() });
      setRooms((Array.isArray(data) ? data : []).sort((a, b) => (a.room_number || 0) - (b.room_number || 0)));
    } catch (e) {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newRoom.name.trim()) return toast.error('Name required');
    const body = { name: newRoom.name.trim() };
    if (newRoom.room_number) body.room_number = parseInt(newRoom.room_number, 10);
    if (newRoom.bags !== '') body.bags = parseInt(newRoom.bags, 10);
    try {
      await axios.post(`${API}/rooms`, body, { headers: auth() });
      toast.success(`Added room "${newRoom.name}"`);
      setAddOpen(false);
      setNewRoom({ name: '', room_number: '', bags: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleRename = async () => {
    if (!renameValue.trim()) return toast.error('Name required');
    try {
      await axios.put(
        `${API}/rooms/${renameRoom.id}/details`,
        { name: renameValue.trim(), bags: renameBags === '' ? null : parseInt(renameBags, 10) },
        { headers: auth() },
      );
      toast.success('Room updated');
      setRenameRoom(null);
      setRenameValue('');
      setRenameBags('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Delete room "${room.name}"?\nThis only works if the room is empty / idle.`)) return;
    try {
      await axios.delete(`${API}/rooms/${room.id}`, { headers: auth() });
      toast.success(`Deleted "${room.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading rooms...</div>;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Rooms — manage your farm&apos;s room layout</CardTitle>
        <Button onClick={() => setAddOpen(true)} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="add-room-btn">
          <Plus className="w-4 h-4 mr-2" />Add Room
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-3">
          You can rename or remove rooms. Deletion is only allowed when no active growing cycle is running on the room.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-gray-200 text-gray-500"><th className="py-2 pr-4">#</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Bags</th><th className="py-2 pr-4">Est. yield/cycle</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Actions</th></tr></thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.id} className="border-b border-gray-100">
                  <td className="py-3 pr-4 font-mono text-gray-500">{r.room_number}</td>
                  <td className="py-3 pr-4 font-medium">{r.name}</td>
                  <td className="py-3 pr-4" data-testid={`room-bags-${r.room_number}`}>{r.bags != null ? r.bags.toLocaleString() : '—'}</td>
                  <td className="py-3 pr-4 text-gray-600">{r.bags != null ? `${(r.bags * 2).toLocaleString()} kg` : '—'}</td>
                  <td className="py-3 pr-4">
                    <Badge className={
                      r.status === 'active' ? 'bg-green-100 text-green-800' :
                      r.status === 'maintenance' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                    }>{r.status}</Badge>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setRenameRoom(r); setRenameValue(r.name); setRenameBags(r.bags != null ? String(r.bags) : ''); }} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(r)} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Room</DialogTitle><DialogDescription>Add a new room to your farm.</DialogDescription></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <div><Label>Name *</Label><Input value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} required placeholder="e.g. Room 17 or Room A" data-testid="room-name" /></div>
            <div><Label>Number of bags</Label><Input type="number" min={0} value={newRoom.bags} onChange={(e) => setNewRoom({ ...newRoom, bags: e.target.value })} placeholder="e.g. 5000" data-testid="room-bags" /></div>
            <div><Label>Room Number (optional — auto if blank)</Label><Input type="number" value={newRoom.room_number} onChange={(e) => setNewRoom({ ...newRoom, room_number: e.target.value })} placeholder="e.g. 17" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="room-submit">Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!renameRoom} onOpenChange={(o) => { if (!o) { setRenameRoom(null); setRenameValue(''); setRenameBags(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Room</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus data-testid="edit-room-name" /></div>
            <div><Label>Number of bags</Label><Input type="number" min={0} value={renameBags} onChange={(e) => setRenameBags(e.target.value)} placeholder="e.g. 5000" data-testid="edit-room-bags" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameRoom(null)}>Cancel</Button>
            <Button onClick={handleRename} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="edit-room-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RoomsManagement;
